import { useState } from "react";
import { Input, Select, DatePicker, Tag, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import "./FilterBar.css";

const { RangePicker } = DatePicker;

/**
 * FilterBar — Global search + column filters + date range + removable chips.
 *
 * Usage:
 *   <FilterBar
 *     searchPlaceholder="Search by name, mobile..."
 *     onSearch={(value) => setSearch(value)}
 *     filters={[
 *       { key: "status", label: "Status", type: "select", options: [
 *         { label: "Active", value: "active" },
 *         { label: "Inactive", value: "inactive" },
 *       ]},
 *       { key: "projectId", label: "Project", type: "select", options: [...] },
 *     ]}
 *     showDateRange
 *     onFilterChange={(allFilters) => fetchData(allFilters)}
 *   />
 */
export default function FilterBar({
  searchPlaceholder = "Search...",
  onSearch,
  filters = [],
  showDateRange = false,
  onFilterChange,
}) {
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState({});

  const handleSearch = (value) => {
    setSearch(value);
    if (onSearch) onSearch(value);
    notifyChange({ ...activeFilters, search: value });
  };

  const handleFilterSelect = (key, value) => {
    const updated = { ...activeFilters };
    if (value === undefined || value === null || value === "") {
      delete updated[key];
    } else {
      updated[key] = value;
    }
    setActiveFilters(updated);
    notifyChange({ ...updated, search });
  };

  const handleDateRange = (dates) => {
    const updated = { ...activeFilters };
    if (dates && dates[0] && dates[1]) {
      updated.dateFrom = dates[0].format("YYYY-MM-DD");
      updated.dateTo = dates[1].format("YYYY-MM-DD");
    } else {
      delete updated.dateFrom;
      delete updated.dateTo;
    }
    setActiveFilters(updated);
    notifyChange({ ...updated, search });
  };

  const removeFilter = (key) => {
    const updated = { ...activeFilters };
    delete updated[key];
    if (key === "dateFrom" || key === "dateTo") {
      delete updated.dateFrom;
      delete updated.dateTo;
    }
    setActiveFilters(updated);
    notifyChange({ ...updated, search });
  };

  const notifyChange = (allFilters) => {
    if (onFilterChange) onFilterChange(allFilters);
  };

  // Build active filter chips
  const filterChips = Object.entries(activeFilters).map(([key, value]) => {
    if (key === "dateTo") return null; // handled by dateFrom chip
    const filterConfig = filters.find((f) => f.key === key);
    let displayLabel = key;
    let displayValue = value;

    if (filterConfig) {
      displayLabel = filterConfig.label;
      const option = filterConfig.options?.find((o) => o.value === value);
      if (option) displayValue = option.label;
    }

    if (key === "dateFrom") {
      displayLabel = "Date";
      displayValue = `${activeFilters.dateFrom} → ${activeFilters.dateTo || ""}`;
    }

    return (
      <Tag
        key={key}
        closable
        onClose={() => removeFilter(key)}
        className="filter-chip"
      >
        {displayLabel}: {displayValue}
      </Tag>
    );
  }).filter(Boolean);

  return (
    <div className="filter-bar">
      <div className="filter-bar-controls">
        <div className="filter-bar-field" style={{ flex: 1 }}>
          <span className="filter-bar-label">Search</span>
          <Input
            placeholder={searchPlaceholder}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
          />
        </div>

        {filters.map((filter) => (
          <div className="filter-bar-field" key={filter.key}>
            <span className="filter-bar-label">{filter.label}</span>
            <Select
              placeholder={filter.label}
              value={activeFilters[filter.key]}
              onChange={(value) => handleFilterSelect(filter.key, value)}
              options={filter.options}
              allowClear
              className="filter-bar-select"
              style={{ minWidth: 140 }}
            />
          </div>
        ))}

        {showDateRange && (
          <div className="filter-bar-field">
            <span className="filter-bar-label">Date Range</span>
            <RangePicker
              onChange={handleDateRange}
              format="DD/MM/YYYY"
              className="filter-bar-date"
            />
          </div>
        )}
      </div>

      {filterChips.length > 0 && (
        <div className="filter-bar-chips">
          {filterChips}
        </div>
      )}
    </div>
  );
}
